import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { Building2, Users, GraduationCap, TrendingUp } from "lucide-react";

interface BranchStats {
  branchId: string;
  branchName: string;
  totalStudents: number;
  activeInstructors: number;
  subjectsCompleted: number;
  examsPassed: number;
  examsFailed: number;
  completionRate: number;
  passRate: number;
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

const fetchBranchComparison = async (): Promise<BranchStats[]> => {
  const [branchesRes, studentsRes, instructorsRes, studentSubjectsRes] = await Promise.all([
    supabase.from("batches").select("id, name"),
    supabase.from("students").select("id, branch_id"),
    supabase.from("instructors").select("id, branch_id, status"),
    supabase.from("student_subjects").select("student_id, status, exam_result"),
  ]);

  if (branchesRes.error) throw branchesRes.error;
  if (studentsRes.error) throw studentsRes.error;
  if (instructorsRes.error) throw instructorsRes.error;
  if (studentSubjectsRes.error) throw studentSubjectsRes.error;

  const branches = branchesRes.data || [];
  const students = studentsRes.data || [];
  const instructors = instructorsRes.data || [];
  const studentSubjects = studentSubjectsRes.data || [];

  const stats: BranchStats[] = [];

  for (const branch of branches) {
    const branchStudents = students.filter(s => s.branch_id === branch.id);
    const branchInstructors = instructors.filter(i => i.branch_id === branch.id && i.status === "Active");
    
    const branchStudentIds = branchStudents.map(s => s.id);
    const branchSubjects = studentSubjects.filter(ss => branchStudentIds.includes(ss.student_id));
    
    const subjectsCompleted = branchSubjects.filter(s => s.status === "Completed").length;
    const examsPassed = branchSubjects.filter(s => s.exam_result === "Pass").length;
    const examsFailed = branchSubjects.filter(s => s.exam_result === "Fail").length;
    const totalExams = examsPassed + examsFailed;

    stats.push({
      branchId: branch.id,
      branchName: branch.name,
      totalStudents: branchStudents.length,
      activeInstructors: branchInstructors.length,
      subjectsCompleted,
      examsPassed,
      examsFailed,
      completionRate: branchSubjects.length > 0 
        ? Math.round((subjectsCompleted / branchSubjects.length) * 100) 
        : 0,
      passRate: totalExams > 0 
        ? Math.round((examsPassed / totalExams) * 100) 
        : 0,
    });
  }

  return stats.filter(s => s.totalStudents > 0);
};

export const BranchComparisonReport = () => {
  const { data: branchStats = [], isLoading } = useQuery({
    queryKey: ["branch-comparison-report"],
    queryFn: fetchBranchComparison,
  });

  if (isLoading) {
    return <Card><CardContent className="p-6">Loading branch comparison...</CardContent></Card>;
  }

  if (branchStats.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No branch data available
        </CardContent>
      </Card>
    );
  }

  const chartData = branchStats.map(b => ({
    name: b.branchName,
    students: b.totalStudents,
    instructors: b.activeInstructors,
    completionRate: b.completionRate,
    passRate: b.passRate,
  }));

  const pieData = branchStats.map(b => ({
    name: b.branchName,
    value: b.totalStudents,
  }));

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {branchStats.map((branch, index) => (
          <Card key={branch.branchId}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4" style={{ color: COLORS[index % COLORS.length] }} />
                <span className="font-medium text-sm truncate">{branch.branchName}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Students</p>
                  <p className="font-bold text-lg">{branch.totalStudents}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Instructors</p>
                  <p className="font-bold text-lg">{branch.activeInstructors}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="h-5 w-5" />
              Students & Instructors by Branch
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Bar dataKey="students" name="Students" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                <Bar dataKey="instructors" name="Instructors" fill="hsl(var(--chart-2))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Student Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Performance Comparison */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Performance Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
              <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} unit="%" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }} 
              />
              <Legend />
              <Bar dataKey="completionRate" name="Completion Rate" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
              <Bar dataKey="passRate" name="Exam Pass Rate" fill="hsl(var(--chart-4))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Detailed Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Detailed Branch Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Branch</th>
                  <th className="text-center p-2">Students</th>
                  <th className="text-center p-2">Instructors</th>
                  <th className="text-center p-2">Subjects Done</th>
                  <th className="text-center p-2">Exams Passed</th>
                  <th className="text-center p-2">Exams Failed</th>
                  <th className="text-center p-2">Completion Rate</th>
                  <th className="text-center p-2">Pass Rate</th>
                </tr>
              </thead>
              <tbody>
                {branchStats.map(branch => (
                  <tr key={branch.branchId} className="border-b">
                    <td className="p-2 font-medium">{branch.branchName}</td>
                    <td className="p-2 text-center">{branch.totalStudents}</td>
                    <td className="p-2 text-center">{branch.activeInstructors}</td>
                    <td className="p-2 text-center">{branch.subjectsCompleted}</td>
                    <td className="p-2 text-center text-green-600">{branch.examsPassed}</td>
                    <td className="p-2 text-center text-destructive">{branch.examsFailed}</td>
                    <td className="p-2 text-center">{branch.completionRate}%</td>
                    <td className="p-2 text-center">{branch.passRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
